import logo from '@/assets/hiredcoach-logo.svg';
import UserAvatar, { userHasCustomAvatar } from '@/components/avatar/UserAvatar';
import { SubscriptionButton } from '@/components/subscription/SubscriptionButton';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useReadiness } from '@/contexts/ReadinessContext';
import { cn } from '@/lib/utils';
import {
  BookOpen,
  Briefcase,
  CheckSquare,
  ChevronLeft,
  Cog,
  DollarSign,
  Home,
  Mic,
  LifeBuoy,
  LogOut,
  Menu,
  Presentation,
  Settings,
  User
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Core navigation items that are always visible
const coreNavigationItems = [
  { name: 'Home', href: '/dashboard', icon: Home },
  { name: 'Me', href: '/me', icon: User },
];

// Profile-dependent navigation items (only shown when user is ready)

const profileDependentItems = [
  { name: 'Jobs', href: '/jobs', icon: Briefcase },
  { name: 'Interviews', href: '/interviews', icon: Mic },
  { name: 'Todos', href: '/todos', icon: CheckSquare },
  { name: 'Pitch Yourself', href: '/pitch', icon: Presentation },
  { name: 'Salary Negotiation', href: '/salary-negotiations', icon: DollarSign },
  {
    name: (
      <>
        <span className="text-[#4f9ecb] font-bold">Coach</span>
        <span className="text-[#334763] font-bold">U</span>
      </>
    ),
    href: '/learn',
    icon: BookOpen
  },
];

const SIDEBAR_PREF_KEY = 'dashboard:sidebar-open';
const SIDEBAR_PREF_TTL_MS = 24 * 60 * 60 * 1000;

type SidebarPreference = {
  isOpen: boolean;
  updatedAt: number;
};

const getFreshSidebarPreference = (): boolean | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(SIDEBAR_PREF_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as SidebarPreference;
    if (typeof parsed?.isOpen !== 'boolean' || typeof parsed?.updatedAt !== 'number') {
      window.localStorage.removeItem(SIDEBAR_PREF_KEY);
      return null;
    }

    if (Date.now() - parsed.updatedAt > SIDEBAR_PREF_TTL_MS) {
      window.localStorage.removeItem(SIDEBAR_PREF_KEY);
      return null;
    }

    return parsed.isOpen;
  } catch {
    window.localStorage.removeItem(SIDEBAR_PREF_KEY);
    return null;
  }
};

const saveSidebarPreference = (isOpen: boolean) => {
  if (typeof window === 'undefined') return;

  const preference: SidebarPreference = {
    isOpen,
    updatedAt: Date.now(),
  };
  window.localStorage.setItem(SIDEBAR_PREF_KEY, JSON.stringify(preference));
};

export const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    const preference = getFreshSidebarPreference();
    if (preference !== null) return preference;
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 768;
  });
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const location = useLocation();
  const { logout, user } = useAuth();
  const { isReady } = useReadiness();
  const hasCustomAvatar = userHasCustomAvatar(user);


  // Removed legacy credits polling; subscriptions now handled via dedicated page

  // Handle responsive behavior
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    saveSidebarPreference(isSidebarOpen);
  }, [isSidebarOpen]);

  const toggleSidebar = () => {
    setIsSidebarOpen((open) => !open);
  };

  const isAdmin = user?.isAdmin || false;

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      {/* Overlay for mobile when sidebar is open */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center justify-between px-4">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            {hasCustomAvatar ? (
              <UserAvatar user={user} className="w-10 h-10 mr-2 border-0" fallback="logo" />
            ) : (
              <img src={logo} alt="HiredCoach Logo" className="w-10 h-10 mr-2" style={{ marginBottom: 0 }} />
            )}
            <span className="font-bold">
              <span className="text-[#4f9ecb]">Hired</span><span className="text-[#334763]">Coach</span>
            </span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        <nav className="mt-5 px-2">
          <div className="space-y-1">
            {/* Core navigation items - always visible */}
            {coreNavigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'group flex items-center rounded-md px-2 py-2 text-sm font-medium',
                    location.pathname === item.href
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  <Icon
                    className={cn(
                      'mr-3 h-5 w-5 flex-shrink-0',
                      location.pathname === item.href
                        ? 'text-gray-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    )}
                  />
                  {item.name}
                </Link>
              );
            })}

            {profileDependentItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'group flex items-center rounded-md px-2 py-2 text-sm font-medium',
                    location.pathname === item.href
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  <Icon
                    className={cn(
                      'mr-3 h-5 w-5 flex-shrink-0',
                      location.pathname === item.href
                        ? 'text-gray-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    )}
                  />
                  {item.name}
                </Link>
              );
            })}

            <Link
              key="settings"
              to="/settings"
              className={cn(
                'group flex items-center rounded-md px-2 py-2 text-sm font-medium',
                location.pathname === "/settings"
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              )}
            >
              <Cog 
                className={cn(
                  'mr-3 h-5 w-5 flex-shrink-0',
                  location.pathname === "/settings"
                    ? 'text-gray-500'
                    : 'text-gray-400 group-hover:text-gray-500'
                )}
              />               
              Settings
            </Link>

            {/* Support as the lowest option */}
            <Link
              key="support"
              to="/contact"
              className={cn(
                'group flex items-center rounded-md px-2 py-2 text-sm font-medium',
                location.pathname === "/contact"
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              )}
            >
              <LifeBuoy
                className={cn(
                  'mr-3 h-5 w-5 flex-shrink-0',
                  location.pathname === "/contact"
                    ? 'text-gray-500'
                    : 'text-gray-400 group-hover:text-gray-500'
                )}
              />
              Support
            </Link>

          </div>
          {/* Mobile-only Subscription button below nav items */}
          {isMobile && (
            <div className="px-0 pt-3">
              <SubscriptionButton isMobile className="w-full" />
            </div>
          )}
        </nav>

        {/* Mobile-only signout button in sidebar */}
        {isMobile && (
          <div className="absolute bottom-4 left-0 right-0 px-2">
            <Button
              variant="ghost"
              onClick={logout}
              className="w-full justify-start text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <LogOut className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400" />
              Sign Out
            </Button>
          </div>
        )}
      </div>

      {/* Main content */}
      <div
        className={cn(
          'flex flex-1 flex-col transition-margin duration-300 ease-in-out',
          // On desktop, adjust margin based on sidebar state; on mobile, always full width
          isMobile ? 'ml-0' : (isSidebarOpen ? 'ml-64' : 'ml-0')
        )}
      >
        {/* Top navigation bar */}
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm">
          {!isSidebarOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8"
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}

          {/* Mobile logo */}
          {isMobile && (
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              {hasCustomAvatar ? (
                <UserAvatar user={user} className="w-8 h-8 mr-2 border-0" fallback="logo" />
              ) : (
                <img src={logo} alt="HiredCoach Logo" className="w-8 h-8 mr-2" />
              )}
              <span className="font-bold text-lg">
                <span className="text-[#4f9ecb]">Hired</span><span className="text-[#334763]">Coach</span>
              </span>
            </Link>
          )}

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {!isMobile && <SubscriptionButton isMobile={isMobile} />}
              <Link to="/settings">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </Link>
              {/* Hide signout button on mobile - it's in the sidebar instead */}
              {!isMobile && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="h-8 w-8"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4">{children}</main>
      </div>
    </div>
  );
};
